"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  useSignupFlowStore,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/hooks/useSignUpFlowStore";
import { SignupFlowSlug, useFreshSignupFlowNavigation } from "@./state";
import {
  MessagingCard,
} from "apps/website/components/feature/MessagingCard/MessagingCard";
import RadioGroup from "apps/website/components/form/RadioButtons/RadioGroup/RadioGroup";
import HorizontalRule from "apps/website/components/base/HorizontalRule/HorizontalRule";
import Icon from "apps/website/components/base/Icon/Icon";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import cx from "apps/website/utils/misc/cx";
import {
  createEmailContactLink,
  renderRichTranslations,
} from "apps/website/utils/i18n/richText";
import Image from "apps/website/components/base/Image/Image";
import Container from "apps/website/components/layout/Container/Container";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import QuoteBanner from "apps/website/components/form/QuoteBanner/QuoteBanner";
import { ContentWrapper } from "apps/website/components/flows/fresh/sign-up";
import { StickyCTA } from "apps/website/components/feature/StickyCTA/StickyCTA";
import { scrollToContent } from "apps/website/utils/website/scrollToContent";
import { useTrackPageView } from "apps/website/contexts/tracking";
import { useEvent } from "libs/hooks/src/lib/event/useEvent";
import { FormName } from "apps/website/types/form";

import {
  CAT_COUNT_OPTIONS,
  CAT_COUNT_SUBMITTED_EVENT,
  type CatCount,
  hasMultiCatDiscount,
} from "./constants";
import { useCatCountForm } from "./hooks/useCatCountForm";

export default function CatCount() {
  const t = useTranslations("Forms.Fresh.SignUp.Steps.CatCount");

  const { createCats, cats } = useSignupFlowStore((state) => ({
    createCats: state.createCats,
    cats: state.cats,
  }));

  const activeCatCount = Object.values(cats).filter((cat) => !cat.softDelete).length;

  const {
    selectedCatCount,
    setSelectedCatCount,
    isValid,
  } = useCatCountForm(activeCatCount);

  const [ hasRadioError, setHasRadioError ] = useState(false);
  const [ showMoreThanFourCatsCard, setShowMoreThanFourCatsCard ] = useState(false);

  const { goToNext, canContinue } = useFreshSignupFlowNavigation(SignupFlowSlug.CAT_COUNT);
  const { dispatchEvent } = useEvent();
  const { quizStarted } = useTrackPageView();
  const posthog = usePostHog();

  useEffect(() => {
    void quizStarted();
  }, [ quizStarted ]);

  useEffect(() => {
    if (selectedCatCount !== null) {
      dispatchEvent("cat_count", {
        category: "manage_fresh",
        label: "cat_count_interaction",
        cat_count: selectedCatCount,
        variant: "test",
      });
    }
  }, [ selectedCatCount, dispatchEvent ]);

  const handleSubmit = () => {
    if (!isValid()) {
      setHasRadioError(true);
      scrollToContent("catCountRadioError");
      return;
    }

    if (canContinue && selectedCatCount !== null) {
      dispatchEvent("cat_count", {
        category: "manage_fresh",
        label: "cat_count_submitted",
        cat_count: selectedCatCount,
        variant: "test",
      });

      /**
      // Set/refresh the person property so the CP Details experiment can target is_single_cat users.
      // setPersonProperties persists it on the profile (durable for later pages/sessions).
      // setPersonPropertiesForFlags feeds the value into flag evaluation and reloads flags,
      // so the experiment re-buckets immediately on change instead of waiting for ingestion (avoids lagging).
      // We re-fire here if the user goes back and changes their cat count.
      */
      const isSingleCat = selectedCatCount === 1;
      posthog.setPersonProperties({ is_single_cat: isSingleCat });
      posthog.setPersonPropertiesForFlags({ is_single_cat: isSingleCat });

      // sendBeacon used so event still leaves the browser if followed by route change or tab close
      posthog.capture(CAT_COUNT_SUBMITTED_EVENT, {
        cat_count: selectedCatCount,
        is_single_cat: isSingleCat,
        form_name: FormName.SIGN_UP_FRESH,
      }, { transport: "sendBeacon" });

      const entries = Array.from(
        { length: selectedCatCount },
        (_, index) => ({
          id: `${index + 1}`,
          cat: { softDelete: false },
        }),
      );
      createCats(entries);
      goToNext();
    }
  };

  const selectedValue = selectedCatCount?.toString() ?? undefined;

  return (
    <>
      <div className="relative z-10 pb-24">
        <Container size="md" padding="none">
          <Image
            image={{
              src: "/images/forms/fresh/cat-section-header.webp",
              width: 992,
              height: 217,
            }}
            alt="Cat with KatKin products"
            className="!bg-transparent"
          />

          <Spacer size="md-lg" />

          <div className="max-w-[393px] mx-auto">
            <QuoteBanner
              quote={t("QuoteBanner.quote")}
              author={t("QuoteBanner.author")}
              image={{ src: "/images/people/Dominique.webp", width: 48, height: 58 }}
              styleOptions={{ bannerFillColour: "light-grey", frameFillColour: "green" }}
            />
          </div>

          <Spacer size="md-lg" />

          <ContentWrapper
            backgroundColour="light-grey"
            styleOptions={{
              paddingX: 4,
              paddingY: 5,
              borderRadius: "md",
              direction: "column",
              justify: "center",
              align: "center",
              gapY: 3,
            }}
          >
            <TextSubtitle size={1} align="center" data-testid="cat-count-heading">
              { t("radioLegend") }
            </TextSubtitle>

            <RadioGroup
              name="catCount"
              selectedValue={selectedValue}
              options={CAT_COUNT_OPTIONS}
              legend={t("radioLegend")}
              srOnlyLegend
              onChange={(value) => {
                setSelectedCatCount(Number(value) as CatCount);
                setHasRadioError(false);
              }}
              styleOptions={{ columns: 4, gap: 2 }}
            />

            { hasRadioError && (
              <MessagingCard
                component="functional"
                description={t("ErrorMessages.radios")}
                icon={{ icon: "warningBold" }}
                styleOptions={{ backgroundColor: "light" }}
                id="catCountRadioError"
              />
            ) }

            { selectedCatCount !== null && hasMultiCatDiscount(selectedCatCount) && (
              <MessagingCard
                component="functional"
                description={t("multiCatDiscount", { count: selectedCatCount })}
                icon={{ icon: "currency", color: "success" }}
                styleOptions={{ backgroundColor: "mid-green" }}
                id="catCountMultiCatDiscount"
              />
            ) }

          </ContentWrapper>
        </Container>

        <button
          onClick={() => setShowMoreThanFourCatsCard((prev) => !prev)}
          className="block w-full md:max-w-[600px] md:mx-auto"
          type="button"
        >
          <HorizontalRule
            styleOptions={{ backgroundColour: "light-grey" }}
          >
            <span className="flex items-center gap-1 underline underline-offset-2 decoration-1 text-sm">
              { t("MoreThanFourCats.button") }
              <Icon
                icon="chevronDownHeavy"
                size="xsmall"
                className={cx(
                  "transition-transform duration-200",
                  showMoreThanFourCatsCard && "rotate-180",
                )}
              />
            </span>
          </HorizontalRule>
        </button>

        { showMoreThanFourCatsCard && (
          <Container size="md" padding="none">
            <MessagingCard
              component="functional"
              description={renderRichTranslations(
                t,
                "MoreThanFourCats.cardBody",
                {
                  email: (chunks: React.ReactNode) => createEmailContactLink(
                    chunks,
                    "meow@katkin.com",
                    2,
                    "normal",
                  ),
                },
              )}
              icon={{ icon: "informationHeavy", color: "default" }}
              styleOptions={{ backgroundColor: "light" }}
              id="catCountMoreThanFourCats"
            />
          </Container>
        ) }
      </div>

      <StickyCTA
        position="fixed"
        button={{
          text: t("ctaButton"),
          onClick: handleSubmit,
          icon: { name: "arrowRightShort", position: "trailing" },
          color: "secondary",
          design: "default",
          ariaLabel: t("ctaAriaLabel"),
          size: "full-width",
        }}
      />
    </>
  );
}
